import {
  addDays,
  addMonths,
  endOfMonth,
  endOfWeek,
  isBefore,
  isSameDay,
  isSameMonth,
  isValid,
  isWithinInterval,
  max,
  min,
  parse,
  startOfMonth,
  startOfWeek,
  subDays,
  subMonths
} from 'date-fns';
import dayjs from 'dayjs';

import { DateRange, DefinedRange } from '../@types/datePicker';

export const identity = <T>(x: T) => x;

export const chunks = <T>(array: ReadonlyArray<T>, size: number): T[][] =>
  Array.from({ length: Math.ceil(array.length / size) }, (_v, i) =>
    array.slice(i * size, i * size + size)
  );

export const combine = (...args: any[]): string => args.filter(identity).join(' ');

// Date
export const getDaysInMonth = (date: Date) => {
  const startWeek = startOfWeek(startOfMonth(date));
  const endWeek = endOfWeek(endOfMonth(date));
  const days: Date[] = [];
  for (let curr: Date = startWeek; isBefore(curr, endWeek); ) {
    days.push(curr);
    curr = addDays(curr, 1);
  }
  return days;
};

export const isStartOfRange = ({ startDate }: DateRange, day: Date) =>
  (startDate && isSameDay(day, startDate)) as boolean;

export const isEndOfRange = ({ endDate }: DateRange, day: Date) =>
  (endDate && isSameDay(day, endDate)) as boolean;

export const inDateRange = ({ startDate, endDate }: DateRange, day: Date) =>
  (startDate &&
    endDate &&
    (isWithinInterval(day, { start: startDate, end: endDate }) ||
      isSameDay(day, startDate) ||
      isSameDay(day, endDate))) as boolean;

export const isRangeSameDay = ({ startDate, endDate }: DateRange) => {
  if (startDate && endDate) {
    return isSameDay(startDate, endDate);
  }
  return false;
};

type Falsy = false | null | undefined | 0 | '';

export const parseOptionalDate = (date: Date | string | Falsy, defaultValue: Date) => {
  if (date) {
    if (typeof date === 'string') {
      const parsed = parse(date, 'MM/dd/yyyy', new Date());
      if (isValid(parsed)) return parsed;
    } else {
      if (isValid(date)) return date;
    }
  }
  return defaultValue;
};

export const getValidatedMonths = (range: DateRange, minDate: Date, maxDate: Date) => {
  const { startDate, endDate } = range;
  if (startDate && endDate) {
    const newStart = max([startDate, minDate]);
    const newEnd = min([endDate, maxDate]);

    return [newStart, isSameMonth(newStart, newEnd) ? addMonths(newStart, 1) : newEnd];
  }
  return [startDate, endDate];
};

const date = dayjs().subtract(1, 'days');
export const splunkDateFormat = 'YYYY-MM-DDTHH:mm:ss';
export const startTime = date.startOf('day').format(splunkDateFormat);
export const endTime = date.endOf('day').format(splunkDateFormat);

export const getYesterday = () => subDays(new Date(), 1);

export const defaultRanges: DefinedRange[] = [
  {
    label: 'Yesterday',
    startDate: getYesterday(),
    endDate: getYesterday(),
    relativeStartTime: getYesterday(),
    relativeEndTime: getYesterday()
  },
  {
    label: 'This Week',
    startDate: startOfWeek(new Date()),
    endDate: getYesterday(),
    relativeStartTime: startOfWeek(new Date()),
    relativeEndTime: getYesterday()
  },
  {
    label: 'Last Week',
    startDate: startOfWeek(subDays(new Date(), 7)),
    endDate: endOfWeek(subDays(new Date(), 7)),
    relativeStartTime: startOfWeek(subDays(new Date(), 7)),
    relativeEndTime: endOfWeek(subDays(new Date(), 7))
  },
  {
    label: 'Last 7 Days',
    startDate: subDays(new Date(), 7),
    endDate: getYesterday(),
    relativeStartTime: subDays(new Date(), 7),
    relativeEndTime: getYesterday()
  },
  {
    label: 'This Month',
    startDate: startOfMonth(new Date()),
    endDate: getYesterday(),
    relativeStartTime: startOfMonth(new Date()),
    relativeEndTime: getYesterday()
  },
  {
    label: 'Last Month',
    startDate: startOfMonth(subMonths(new Date(), 1)),
    endDate: endOfMonth(subMonths(new Date(), 1)),
    relativeStartTime: startOfMonth(subMonths(new Date(), 1)),
    relativeEndTime: endOfMonth(subMonths(new Date(), 1))
  }
];
